import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { opportunitiesApi, api, logActivity } from '../api/client';
import { useLens } from '../hooks/useLens';
import { notify } from '../api/client';
import { stageColor, stageLabel } from '../lib/stages';
import { markOnboardingStep } from '../lib/onboarding';

export default function Opportunities() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lens } = useLens();
  const isSeller = lens === 'seller';
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [agency, setAgency] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [lifecycleMap, setLifecycleMap] = useState<Record<string, string>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadProgress, setCsvUploadProgress] = useState('');
  const [csvUploadResult, setCsvUploadResult] = useState<any>(null);
  const [csvAutoCreateCases, setCsvAutoCreateCases] = useState(false);
  const [csvAutoCreateLifecycle, setCsvAutoCreateLifecycle] = useState(false);
  const csvFileRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '', notice_id: '', description: '', agency: '', naics_code: '', budget_amount: '', budget_currency: 'USD',
    related_notice: '', contract_opportunity_type: '', contract_line_item_number: '',
    inactive_date: '', inactive_policy: '', date_offers_due: '', published_date: '',
    department: '', sub_tier: '', office: '', procurement_case_id: '',
    set_aside: '', product_service_code: '', place_of_performance: '', initiative: 'None',
    contracting_organization: '',
    poc_primary_name: '', poc_primary_email: '', poc_primary_phone: '',
    poc_alt_name: '', poc_alt_email: '', poc_alt_phone: '',
    contracting_office_address: '',
    notes: '',
  });
  const [orgOptions, setOrgOptions] = useState<Array<{ label: string; stack: string }>>([]);

  // Load contracting-organization options (drives checklist add-ons + regulations)
  useEffect(() => {
    opportunitiesApi.orgOptions()
      .then((r: any) => setOrgOptions(r?.org_options || []))
      .catch(() => setOrgOptions([]));
  }, []);

  // Prefill from SAM.gov Library navigation
  useEffect(() => {
    const state = location.state as any;
    if (state?.prefill) {
      const p = state.prefill;
      setFormData(prev => ({
        ...prev,
        title: p.title || prev.title,
        notice_id: p.notice_id || prev.notice_id,
        agency: p.agency || prev.agency,
        naics_code: p.naics_code || prev.naics_code,
        product_service_code: p.psc_code || prev.product_service_code,
        set_aside: p.set_aside || prev.set_aside,
        contract_opportunity_type: p.notice_type || prev.contract_opportunity_type,
        date_offers_due: p.response_deadline || prev.date_offers_due,
        published_date: p.publication_date || prev.published_date,
        description: p.description || prev.description,
        poc_primary_name: p.poc_primary_name || prev.poc_primary_name,
        budget_amount: p.budget || prev.budget_amount,
        place_of_performance: p.place_of_performance || prev.place_of_performance,
        notes: p.notes || prev.notes,
      }));
      setShowCreateModal(true);
      // Clear the state so refreshing doesn't re-prefill
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { page: String(page), page_size: '20' };
      if (search) params.search = search;
      if (agency) params.agency = agency;
      if (statusFilter) params.status = statusFilter;
      // Seller lens: only show seller pursuits (backend filters by pursuit_side).
      if (isSeller) params.pursuit_side = 'seller';
      const [res, lcRes] = await Promise.all([
        opportunitiesApi.list(params),
        api.get<any>('/lifecycle').catch(() => ({ items: [] })),
      ]);
      let filteredItems = res.items || [];
      // Client-side status filter (in case backend doesn't filter)
      if (statusFilter) {
        filteredItems = filteredItems.filter((o: any) => o.status === statusFilter);
      }
      // Sort by deadline (closest first), items without deadline go to end
      filteredItems.sort((a: any, b: any) => {
        const aDate = a.response_deadline || a.date_offers_due || '';
        const bDate = b.response_deadline || b.date_offers_due || '';
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(aDate).getTime() - new Date(bDate).getTime();
      });
      setItems(filteredItems);
      setTotal(res.total || filteredItems.length);

      // Build a map of opportunity_id -> lifecycle stage
      const lcItems = lcRes?.items || lcRes || [];
      const map: Record<string, string> = {};
      if (Array.isArray(lcItems)) {
        lcItems.forEach((lc: any) => {
          if (lc.opportunity_id) {
            map[lc.opportunity_id] = lc.current_stage || lc.stage || 'intake';
          }
        });
      }
      setLifecycleMap(map);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, agency, statusFilter, isSeller]);

  useEffect(() => { load(); }, [load]);

  const handleCreateOpportunity = async () => {
    if (!formData.title || !formData.agency) {
      setCreateError('Title and Agency are required');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const result = await opportunitiesApi.create({
        // Seller lens: created items are seller pursuits (backend also derives this
        // from org_type, but set it explicitly so it's unambiguous).
        ...(isSeller ? { pursuit_side: 'seller' } : {}),
        title: formData.title,
        notice_id: formData.notice_id,
        description: formData.description,
        agency: formData.agency,
        contracting_organization: formData.contracting_organization,
        naics_code: formData.naics_code,
        budget_amount: formData.budget_amount ? parseInt(formData.budget_amount) : null,
        budget_currency: formData.budget_currency,
        related_notice: formData.related_notice,
        procurement_case_id: formData.procurement_case_id,
        contract_opportunity_type: formData.contract_opportunity_type,
        contract_line_item_number: formData.contract_line_item_number,
        inactive_date: formData.inactive_date,
        inactive_policy: formData.inactive_policy,
        date_offers_due: formData.date_offers_due,
        published_date: formData.published_date,
        department: formData.department,
        sub_tier: formData.sub_tier,
        office: formData.office,
        set_aside: formData.set_aside,
        product_service_code: formData.product_service_code,
        place_of_performance: formData.place_of_performance,
        initiative: formData.initiative,
        poc_primary_name: formData.poc_primary_name,
        poc_primary_email: formData.poc_primary_email,
        poc_primary_phone: formData.poc_primary_phone,
        poc_alt_name: formData.poc_alt_name,
        poc_alt_email: formData.poc_alt_email,
        poc_alt_phone: formData.poc_alt_phone,
        contracting_office_address: formData.contracting_office_address,
        notes: formData.notes,
      });

      // Handle duplicate detection
      if (result.duplicate_detected) {
        const existingId = result.existing_opportunity?.id;
        if (existingId) {
          setCreateError(`This notice already exists. Opening existing opportunity...`);
          setTimeout(() => { navigate(`/opportunities/${existingId}`); }, 1000);
          return;
        } else {
          setCreateError('A notice with this ID already exists. Change the Notice ID or clear it to create a new one.');
          setCreating(false);
          return;
        }
      }

      markOnboardingStep('create_opportunity');

      // Auto-create a matching Lifecycle entry with all opportunity data
      try {
        await api.post<any>('/lifecycle', {
          title: formData.title,
          opportunity_id: result.opportunity_id,
          notice_id: formData.notice_id,
          contract_number: formData.notice_id || '',
          agency: formData.agency,
          contracting_organization: formData.contracting_organization,
          vendor_name: '',
          estimated_value: formData.budget_amount ? `$${Number(formData.budget_amount).toLocaleString()}` : '',
          contract_type: formData.contract_opportunity_type || '',
          period_of_performance: '',
          related_notice: formData.related_notice,
          contract_opportunity_type: formData.contract_opportunity_type,
          contract_line_item_number: formData.contract_line_item_number,
          inactive_date: formData.inactive_date,
          inactive_policy: formData.inactive_policy,
          date_offers_due: formData.date_offers_due,
          published_date: formData.published_date,
          department: formData.department,
          sub_tier: formData.sub_tier,
          office: formData.office,
          set_aside: formData.set_aside,
          product_service_code: formData.product_service_code,
          naics_code: formData.naics_code,
          place_of_performance: formData.place_of_performance,
          initiative: formData.initiative,
          poc_primary_name: formData.poc_primary_name,
          poc_primary_email: formData.poc_primary_email,
          poc_primary_phone: formData.poc_primary_phone,
          poc_alt_name: formData.poc_alt_name,
          poc_alt_email: formData.poc_alt_email,
          poc_alt_phone: formData.poc_alt_phone,
          contracting_office_address: formData.contracting_office_address,
        });
      } catch (lcErr) {
        console.warn('Lifecycle auto-create failed (non-blocking):', lcErr);
      }

      setShowCreateModal(false);
      setFormData({
        title: '', notice_id: '', description: '', agency: '', naics_code: '', budget_amount: '', budget_currency: 'USD',
        related_notice: '', contract_opportunity_type: '', contract_line_item_number: '',
        inactive_date: '', inactive_policy: '', date_offers_due: '', published_date: '',
        department: '', sub_tier: '', office: '', procurement_case_id: '',
        set_aside: '', product_service_code: '', place_of_performance: '', initiative: 'None',
        contracting_organization: '',
        poc_primary_name: '', poc_primary_email: '', poc_primary_phone: '',
        poc_alt_name: '', poc_alt_email: '', poc_alt_phone: '',
        contracting_office_address: '',
        notes: '',
      });
      navigate(`/opportunities/${result.opportunity_id}`);

      // Fire notification
      notify(`New Opportunity: ${formData.title}`, {
        type: 'info',
        source: 'opportunities',
        source_id: result.opportunity_id,
        description: `Agency: ${formData.agency}${formData.date_offers_due ? ' | Due: ' + formData.date_offers_due : ''}`,
        action_url: `/opportunities/${result.opportunity_id}`,
      });
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOpportunity = async (id: string) => {
    try {
      await opportunitiesApi.delete(id);
      setDeleteConfirm(null);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await opportunitiesApi.update(id, { status: newStatus });
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setCsvUploadResult({ error: 'File too large. Maximum size is 10 MB.' });
      if (csvFileRef.current) csvFileRef.current.value = '';
      return;
    }
    setCsvUploading(true);
    setCsvUploadResult(null);
    setCsvUploadProgress('Reading file...');
    try {
      const text = await file.text();
      // CSV-aware row splitting
      const rows: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') { inQuotes = !inQuotes; current += ch; }
        else if ((ch === '\n' || ch === '\r') && !inQuotes) {
          if (ch === '\r' && text[i + 1] === '\n') i++;
          if (current.trim().length > 0) rows.push(current);
          current = '';
        } else { current += ch; }
      }
      if (current.trim().length > 0) rows.push(current);

      const header = rows[0];
      const dataRows = rows.slice(1);
      const CHUNK_BYTE_LIMIT = 3_500_000;
      const chunks: string[][] = [];
      let currentChunk: string[] = [];
      let currentSize = header.length + 1;
      for (const row of dataRows) {
        const rowSize = row.length + 1;
        if (currentSize + rowSize > CHUNK_BYTE_LIMIT && currentChunk.length > 0) {
          chunks.push(currentChunk);
          currentChunk = [];
          currentSize = header.length + 1;
        }
        currentChunk.push(row);
        currentSize += rowSize;
      }
      if (currentChunk.length > 0) chunks.push(currentChunk);

      let totalParsed = 0, totalCreated = 0, totalSkipped = 0;
      const allErrors: string[] = [];
      for (let i = 0; i < chunks.length; i++) {
        setCsvUploadProgress(`Uploading batch ${i + 1} of ${chunks.length}...`);
        const csvChunk = [header, ...chunks[i]].join('\n');
        const base64Chunk = btoa(unescape(encodeURIComponent(csvChunk)));
        try {
          const result = await api.post<any>('/opportunities/csv-upload', { csv_data: base64Chunk, create_cases: csvAutoCreateCases, create_lifecycle: csvAutoCreateLifecycle });
          totalParsed += result.rows_parsed || 0;
          totalCreated += result.rows_created || 0;
          totalSkipped += result.rows_skipped || 0;
          if (result.errors?.length) allErrors.push(...result.errors.slice(0, 5));
        } catch (err: any) {
          allErrors.push(`Batch ${i + 1} failed: ${err.message}`);
        }
      }
      setCsvUploadResult({ rows_parsed: totalParsed, rows_created: totalCreated, rows_skipped: totalSkipped, errors: allErrors.length > 0 ? allErrors : undefined });
      load();
    } catch (err: any) {
      setCsvUploadResult({ error: err.message });
    }
    setCsvUploading(false);
    setCsvUploadProgress('');
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#374151', background: '#f9fafb' };
  const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #e5e7eb' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>{isSeller ? 'My Pursuits' : 'Opportunities'}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={csvFileRef} type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} id="opp-csv-upload" />
          <label htmlFor="opp-csv-upload" style={{ padding: '10px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: csvUploading ? 0.6 : 1 }}>
            {csvUploading ? (csvUploadProgress || 'Uploading...') : 'Upload CSV'}
          </label>
          <button onClick={() => setShowCreateModal(true)} style={{ padding: '10px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            + Create Opportunity
          </button>
        </div>
      </div>

      {/* CSV Upload Options */}
      <div style={{ display: 'none' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#374151' }}>
          <input type="checkbox" checked={csvAutoCreateCases} onChange={e => setCsvAutoCreateCases(e.target.checked)} />
          Auto-create Procurement Cases
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#374151' }}>
          <input type="checkbox" checked={csvAutoCreateLifecycle} onChange={e => setCsvAutoCreateLifecycle(e.target.checked)} />
          Auto-create Lifecycle entries
        </label>
      </div>

      {/* CSV Upload Result */}
      {csvUploadResult && (
        <div role="status" aria-live="polite" style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13, background: csvUploadResult.error ? '#fef2f2' : '#f0fdf4', border: `1px solid ${csvUploadResult.error ? '#fecaca' : '#bbf7d0'}`, color: csvUploadResult.error ? '#991b1b' : '#166534' }}>
          {csvUploadResult.error
            ? `Upload failed: ${csvUploadResult.error}`
            : `Imported ${csvUploadResult.rows_created} opportunities (${csvUploadResult.rows_skipped} skipped as duplicates/inactive, ${csvUploadResult.rows_parsed} total parsed)${csvUploadResult.errors?.length ? ` — ${csvUploadResult.errors.length} errors` : ''}`
          }
          <button onClick={() => setCsvUploadResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', color: 'inherit' }}>dismiss</button>
        </div>
      )}

      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 800, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ fontSize: 16, marginBottom: 16 }}>Create New Opportunity</h3>
            {createError && <div role="alert" style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{createError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: 'span 3' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Title *</label>
                <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., IT Services Procurement" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: 'span 3', padding: '10px 14px', background: '#f5f3ff', borderRadius: 8, border: '1px solid #c4b5fd' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#5b21b6' }}>Procurement Case (groups RFI → RFQ → Award)</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input value={formData.procurement_case_id} onChange={e => setFormData({...formData, procurement_case_id: e.target.value})} placeholder="Leave blank to auto-create, or enter existing case # (e.g., PROC-2026-000001)" style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>Links this notice to a procurement case. If blank, a new case will be created. Use an existing case # to group RFI, RFQ, and Award under one procurement.</div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Related Notice</label>
                <input value={formData.related_notice} onChange={e => setFormData({...formData, related_notice: e.target.value})} placeholder="Related notice #" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Contract Opportunity Type</label>
                <select value={formData.contract_opportunity_type} onChange={e => setFormData({...formData, contract_opportunity_type: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }}>
                  <option value="">Select...</option>
                  <option value="Solicitation">Solicitation</option>
                  <option value="Presolicitation">Presolicitation</option>
                  <option value="Combined Synopsis/Solicitation">Combined Synopsis/Solicitation</option>
                  <option value="Sources Sought">Sources Sought</option>
                  <option value="Special Notice">Special Notice</option>
                  <option value="Award Notice">Award Notice</option>
                  <option value="Intent to Bundle">Intent to Bundle</option>
                  <option value="Justification">Justification</option>
                </select>
              </div>
              <div style={{ gridColumn: 'span 3' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="e.g., Seeking cloud migration services" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box', minHeight: 60 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Contract Line Item Number</label>
                <input value={formData.contract_line_item_number} onChange={e => setFormData({...formData, contract_line_item_number: e.target.value})} placeholder="e.g., CLIN 0001" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Published Date</label>
                <input type="date" value={formData.published_date} onChange={e => setFormData({...formData, published_date: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Date Offers Due</label>
                <input type="date" value={formData.date_offers_due} onChange={e => setFormData({...formData, date_offers_due: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Inactive Date</label>
                <input type="date" value={formData.inactive_date} onChange={e => setFormData({...formData, inactive_date: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Inactive Policy</label>
                <select value={formData.inactive_policy} onChange={e => setFormData({...formData, inactive_policy: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }}>
                  <option value="">Select...</option>
                  <option value="15 days after date offers due">15 days after date offers due</option>
                  <option value="30 days after date offers due">30 days after date offers due</option>
                  <option value="Manual">Manual</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Sub-tier</label>
                <input value={formData.sub_tier} onChange={e => setFormData({...formData, sub_tier: e.target.value})} placeholder="e.g., Army" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Office</label>
                <input value={formData.office} onChange={e => setFormData({...formData, office: e.target.value})} placeholder="e.g., ACC-APG" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Agency *</label>
                <select value={formData.agency} onChange={e => setFormData({...formData, agency: e.target.value, contracting_organization: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }}>
                  <option value="">Select agency…</option>
                  {orgOptions.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
                </select>
                {formData.agency
                  ? <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>{(orgOptions.find(o => o.label === formData.agency) || {}).stack || ''}</div>
                  : <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Determines the FAR supplement, checklist add-ons, and clause scoping.</div>}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Original Set Aside</label>
                <select value={formData.set_aside} onChange={e => setFormData({...formData, set_aside: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }}>
                  <option value="">No Set Aside used</option>
                  <option value="SBA">Total Small Business Set-Aside</option>
                  <option value="SBP">Partial Small Business Set-Aside</option>
                  <option value="8A">8(a) Set-Aside</option>
                  <option value="8AN">8(a) Sole Source</option>
                  <option value="HZC">HUBZone Set-Aside</option>
                  <option value="HZS">HUBZone Sole Source</option>
                  <option value="SDVOSBC">SDVOSB Set-Aside</option>
                  <option value="SDVOSBS">SDVOSB Sole Source</option>
                  <option value="WOSB">WOSB Set-Aside</option>
                  <option value="WOSBSS">WOSB Sole Source</option>
                  <option value="EDWOSB">EDWOSB Set-Aside</option>
                  <option value="EDWOSBSS">EDWOSB Sole Source</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Product Service Code</label>
                <input value={formData.product_service_code} onChange={e => setFormData({...formData, product_service_code: e.target.value})} placeholder="e.g., 7B20 - IT AND TELECOM" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>NAICS Code</label>
                <input value={formData.naics_code} onChange={e => setFormData({...formData, naics_code: e.target.value})} placeholder="e.g., 541512" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Place of Performance</label>
                <input value={formData.place_of_performance} onChange={e => setFormData({...formData, place_of_performance: e.target.value})} placeholder="e.g., Washington, DC" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Initiative</label>
                <select value={formData.initiative} onChange={e => setFormData({...formData, initiative: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }}>
                  <option value="None">None</option>
                  <option value="DisasterRelief">Disaster Relief</option>
                  <option value="Made in America">Made in America</option>
                  <option value="Recovery Act">Recovery Act</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Budget Amount</label>
                <input value={formData.budget_amount} onChange={e => setFormData({...formData, budget_amount: e.target.value})} placeholder="e.g., 500000" type="number" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              {/* Primary Point of Contact */}
              <div style={{ gridColumn: 'span 3', borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Primary Point of Contact</label>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Name</label>
                <input value={formData.poc_primary_name} onChange={e => setFormData({...formData, poc_primary_name: e.target.value})} placeholder="Full name" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Email</label>
                <input type="email" value={formData.poc_primary_email} onChange={e => setFormData({...formData, poc_primary_email: e.target.value})} placeholder="email@agency.gov" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Phone Number</label>
                <input type="tel" value={formData.poc_primary_phone} onChange={e => setFormData({...formData, poc_primary_phone: e.target.value})} placeholder="(xxx) xxx-xxxx" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              {/* Alternative Point of Contact */}
              <div style={{ gridColumn: 'span 3', borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Alternative Point of Contact</label>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Name</label>
                <input value={formData.poc_alt_name} onChange={e => setFormData({...formData, poc_alt_name: e.target.value})} placeholder="Full name" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Email</label>
                <input type="email" value={formData.poc_alt_email} onChange={e => setFormData({...formData, poc_alt_email: e.target.value})} placeholder="email@agency.gov" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Phone Number</label>
                <input type="tel" value={formData.poc_alt_phone} onChange={e => setFormData({...formData, poc_alt_phone: e.target.value})} placeholder="(xxx) xxx-xxxx" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              {/* Contracting Office Address */}
              <div style={{ gridColumn: 'span 3' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Contracting Office Address</label>
                <input value={formData.contracting_office_address} onChange={e => setFormData({...formData, contracting_office_address: e.target.value})} placeholder="Full address" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginTop: 4, boxSizing: 'border-box' }} />
              </div>
              {/* Notes (AI-extracted data and additional info) */}
              {formData.notes && (
                <div style={{ gridColumn: 'span 3' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#5b21b6' }}>📋 AI-Extracted Notes</label>
                  <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} style={{ width: '100%', padding: '10px 12px', border: '1px solid #c4b5fd', borderRadius: 6, fontSize: 12, marginTop: 4, boxSizing: 'border-box', minHeight: 120, resize: 'vertical', fontFamily: 'monospace', background: '#faf5ff', lineHeight: 1.6 }} />
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>These notes contain AI-extracted data (scope, FAR clauses, deliverables, etc.) that will be saved with the opportunity.</div>
                </div>
              )}
              <div style={{ gridColumn: 'span 3', display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setShowCreateModal(false)} style={{ flex: 1, padding: '10px 16px', background: '#d1d5db', color: '#1f2937', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleCreateOpportunity} disabled={creating} style={{ flex: 1, padding: '10px 16px', background: creating ? '#93c5fd' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: creating ? 'default' : 'pointer' }}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          placeholder="Search Notice ID, solicitation number, title, agency, or procurement..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          onKeyDown={e => { if (e.key === 'Enter') { if (search.trim()) logActivity('search', { query: search.trim(), page: 'Opportunities' }); load(); } }}
          aria-label="Search opportunities"
        />
        <select
          style={{ width: 150, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          aria-label="Status filter"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button onClick={load} style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 14 }}>
          Search
        </button>
      </div>

      {error && <div role="alert" style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: '#6b7280', marginBottom: 12, fontSize: 13 }}>Loading...</div>}

      <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>
          {total} {isSeller ? 'pursuits' : 'opportunities'} found
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Case', 'Notice ID', 'Type', 'Title', 'Agency', 'Main POC', 'Status', 'Ver.', 'Deadline', 'Actions'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr><td colSpan={10} style={{ ...td, color: '#9ca3af', textAlign: 'center', padding: 32 }}>
                {isSeller
                  ? 'No pursuits yet. Create a pursuit from a notice in Discovery, or add one here.'
                  : 'No opportunities found. Create one or trigger ingestion from the Admin panel.'}
              </td></tr>
            ) : items.map((o: any) => (
              <tr key={o.id} style={{ cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <td style={{ ...td, fontSize: 10 }}>
                  {o.procurement_case_id ? <Link to={`/opportunities/${o.id}`} style={{ padding: '2px 6px', background: '#ede9fe', color: '#5b21b6', borderRadius: 4, fontFamily: 'monospace', fontSize: 9, fontWeight: 600, textDecoration: 'none' }}>{o.procurement_case_id}</Link> : <span style={{ color: '#d1d5db' }}>—</span>}
                </td>
                <td style={td}>
                  <Link to={`/opportunities/${o.id}`} style={{ color: '#2563eb', fontWeight: 600, fontSize: 12, fontFamily: 'monospace' }}>{o.notice_id || '—'}</Link>
                  {o.internal_opp_id && <div style={{ fontSize: 9, color: '#9ca3af' }}>{o.internal_opp_id}</div>}
                </td>
                <td style={td}>
                  {o.notice_type ? (
                    <span style={{ padding: '2px 6px', borderRadius: 9999, fontSize: 10, fontWeight: 600,
                      background: (o.notice_type || '').toLowerCase().includes('solicitation') ? '#fce7f3' :
                        (o.notice_type || '').toLowerCase().includes('rfi') || (o.notice_type || '').toLowerCase().includes('sources') ? '#dbeafe' :
                        (o.notice_type || '').toLowerCase().includes('award') ? '#d1fae5' : '#f3f4f6',
                      color: (o.notice_type || '').toLowerCase().includes('solicitation') ? '#9d174d' :
                        (o.notice_type || '').toLowerCase().includes('rfi') || (o.notice_type || '').toLowerCase().includes('sources') ? '#1e40af' :
                        (o.notice_type || '').toLowerCase().includes('award') ? '#065f46' : '#374151',
                    }}>{o.notice_type}</span>
                  ) : <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>}
                </td>
                <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Link to={`/opportunities/${o.id}`} style={{ color: '#1f2937', fontWeight: 500, textDecoration: 'none', fontSize: 12 }}>{o.title?.substring(0, 60)}</Link>
                </td>
                <td style={{ ...td, color: '#6b7280', fontSize: 11 }}>{o.agency || '—'}</td>
                <td style={{ ...td, fontSize: 11 }}>
                  {o.poc_primary_name ? (
                    <div>
                      <div style={{ color: '#1f2937', fontWeight: 500 }}>{o.poc_primary_name}</div>
                      {o.poc_primary_email && <div style={{ fontSize: 10, color: '#6b7280' }}>{o.poc_primary_email}</div>}
                    </div>
                  ) : <span style={{ color: '#9ca3af' }}>—</span>}
                </td>
                <td style={td}>
                  <select
                    value={o.status || 'active'}
                    onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); handleStatusChange(o.id, e.target.value); }}
                    style={{ padding: '2px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, border: '1px solid #d1d5db', cursor: 'pointer',
                      background: o.status === 'active' ? '#d1fae5' : o.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                      color: o.status === 'active' ? '#065f46' : o.status === 'draft' ? '#92400e' : '#6b7280',
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </td>
                <td style={{ ...td, textAlign: 'center', fontSize: 11 }}>
                  {o.current_version ? <span style={{ padding: '1px 5px', background: '#f3f4f6', borderRadius: 3, fontSize: 10 }}>v{o.current_version}</span> : '—'}
                </td>
                <td style={{ ...td, color: '#6b7280', fontSize: 11 }}>{o.response_deadline ? (typeof o.response_deadline === 'string' ? o.response_deadline.substring(0, 10) : new Date(o.response_deadline).toLocaleDateString()) : '—'}</td>
                <td style={td}>
                  {deleteConfirm === o.id ? (
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button onClick={e => { e.stopPropagation(); handleDeleteOpportunity(o.id); }} aria-label="Confirm delete" style={{ padding: '3px 8px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Confirm</button>
                      <button onClick={e => { e.stopPropagation(); setDeleteConfirm(null); }} aria-label="Cancel delete" style={{ padding: '3px 8px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                    </span>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm(o.id); }} style={{ padding: '3px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500 }} title="Delete opportunity" aria-label="Delete opportunity">
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > 20 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}>← Prev</button>
            <span style={{ padding: '4px 8px', fontSize: 13 }}>Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} aria-label="Next page" style={{ padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    active: ['#d1fae5', '#065f46'], completed: ['#d1fae5', '#065f46'],
    pending: ['#fef3c7', '#92400e'], running: ['#dbeafe', '#1e40af'],
    failed: ['#fee2e2', '#991b1b'], inactive: ['#f3f4f6', '#6b7280'],
    draft: ['#fef3c7', '#92400e'],
  };
  const [bg, text] = map[status] || ['#f3f4f6', '#374151'];
  return <span style={{ background: bg, color: text, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{status || '—'}</span>;
}

function LifecycleBadge({ stage }: { stage?: string }) {
  if (!stage) return <span style={{ color: '#9ca3af', fontSize: 11 }}>—</span>;
  const { bg, color: text } = stageColor(stage);
  const label = stageLabel(stage);
  return <span style={{ background: bg, color: text, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{label}</span>;
}
